import React, { useReducer, createContext, useCallback, useMemo } from "react";
import PropTypes from "prop-types";
import { movieReducer, movieInitialState } from "../reducers";
import constant from "../constants/movieConstant";
import { movieService } from "../services";
import { toast } from "react-toastify";
import { history } from "../tools";

export const MovieContext = createContext({
    state: movieInitialState,
    actions: {
        get: () => { },
        add: () => { },
        should: () => { },
        addWatchList: () => { },
        search: () => { },
        paginate: () => { },
        reset: () => { },
        goToDetail: () => { }
    }
});

export const MovieContextProvider = ({ children }) => {
    const [state, dispatch] = useReducer(movieReducer, movieInitialState);

    const get = useCallback((id, isRequest = true) => {
        if (isRequest) dispatch({ type: constant.GET_REQUEST });

        movieService
            .get(id)
            .then((response) => {
                if (!response.success) {
                    toast.error(response.message);
                    dispatch({ type: constant.GET_FAIL });
                    return;
                }
                dispatch({ type: constant.GET_SUCCESS, payload: response.data });
            })
            .catch(() => {
                toast.error("the movie couldn't be fetched!");
                dispatch({ type: constant.GET_FAIL });
            });
    }, []);

    const add = useCallback((movie, onSuccess) => {
        dispatch({ type: constant.ADD_REQUEST });

        movieService
            .add(movie)
            .then((response) => {
                if (!response.success) {
                    toast.error(response.message);
                    dispatch({ type: constant.ADD_FAIL });
                    return;
                }
                dispatch({ type: constant.ADD_SUCCESS, payload: response.data });
                if (onSuccess) onSuccess(response.data);
            })
            .catch(() => {
                toast.error("the movie couldn't be added!");
                dispatch({ type: constant.ADD_FAIL });
            });
    }, []);

    const should = useCallback((dto, onSuccess) => {
        dispatch({ type: constant.SHOULD_REQUEST });

        movieService
            .should(dto)
            .then((response) => {
                if (!response.success) {
                    toast.error(response.message);
                    dispatch({ type: constant.SHOULD_FAIL });
                    return;
                }
                dispatch({ type: constant.SHOULD_SUCCESS, payload: response.data });
                if (onSuccess) onSuccess();
            })
            .catch(() => {
                toast.error("the movie couldn't be rated!");
                dispatch({ type: constant.SHOULD_FAIL });
            });
    }, []);

    const addWatchList = useCallback((dto, onSuccess) => {
        dispatch({ type: constant.ADD_LIST_REQUEST });

        movieService
            .addWatchList(dto)
            .then((response) => {
                if (!response.success) {
                    toast.error(response.message);
                    dispatch({ type: constant.ADD_LIST_FAIL });
                    return;
                }
                dispatch({ type: constant.ADD_LIST_SUCCESS, payload: response.data });
                if (onSuccess) onSuccess();
            })
            .catch(() => {
                toast.error("The movie couldn't be added to your watching list!");
                dispatch({ type: constant.ADD_LIST_FAIL });
            });
    }, []);

    const search = useCallback((key) => {
        dispatch({ type: constant.SEARCH_REQUEST });

        movieService
            .search(key)
            .then((response) => {
                if (!response.success) {
                    toast.error(response.message);
                    dispatch({ type: constant.SEARCH_FAIL });
                    return;
                }
                dispatch({ type: constant.SEARCH_SUCCESS, payload: response.data });
            })
            .catch(() => {
                toast.error("movies couldn't be fetched!");
                dispatch({ type: constant.SEARCH_FAIL });
            });
    }, []);

    const paginate = useCallback((dto, onSuccess) => {
        const payload = { limit: 12, ...dto };
        dispatch({ type: constant.PAGINATE_REQUEST });

        movieService
            .paginate(payload)
            .then((response) => {
                if (!response.success) {
                    toast.error(response.message);
                    dispatch({ type: constant.PAGINATE_FAIL });
                    return;
                }
                dispatch({ type: constant.PAGINATE_SUCCESS, payload: response.data });
                if (onSuccess) onSuccess();
            })
            .catch(() => {
                toast.error("movies couldn't be fetched!");
                dispatch({ type: constant.PAGINATE_FAIL });
            });
    }, []);

    const goToDetail = useCallback((id) => {
        history.push(`/movie/${id}`);
    }, []);

    const reset = useCallback(() => {
        dispatch({ type: constant.RESET });
    }, []);

    const actions = useMemo(
        () => ({
            add,
            search,
            paginate,
            get,
            should,
            reset,
            addWatchList,
            goToDetail
        }),
        [add, search, paginate, get, should, reset, addWatchList, goToDetail]
    );

    const value = useMemo(() => ({ state, actions }), [state, actions]);

    return <MovieContext.Provider value={value}>{children}</MovieContext.Provider>;
};

MovieContextProvider.propTypes = {
    children: PropTypes.node.isRequired
};
